import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  IconButton, Stack, TableCell, TableRow, Typography,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add, Delete as DeleteIcon, Edit as EditIcon, Refresh } from '@mui/icons-material';
import { reportCardAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { DataTable, TD_SX, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import ConductCategoryFormDialog from './forms/ConductCategoryFormDialog';
import BulkConductCategoryDialog from './forms/BulkConductCategoryDialog';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const getErrorMessage = (e, fallback = 'Operation failed') =>
  e?.response?.data?.message || e?.response?.data?.error || fallback;
const getCategoryName = (c) => c?.category_name || c?.name || 'Unknown';
const getCategoryCode = (c) => c?.category_code || c?.code || '-';

const INITIAL_FORM = { category_code: '', category_name: '', display_order: '', description: '', is_active: true };
const COLUMNS = ['Code', 'Name', 'Display Order', 'Description', 'Status', 'Actions'];
const PAGE_SIZE = 15;

export default function ConductCategories() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      setCategories(extractCollection(await reportCardAPI.getConductCategories()));
    } catch (e) {
      showSnackbar(getErrorMessage(e, 'Failed to load categories'), 'error');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const resetForm = () => { setForm(INITIAL_FORM); setEditingCategory(null); setDialog(false); };

  const handleEdit = (cat) => {
    setEditingCategory(cat);
    setForm({
      category_code: cat.category_code || cat.code || '',
      category_name: cat.category_name || cat.name || '',
      display_order: cat.display_order ?? '',
      description: cat.description || '',
      is_active: cat.is_active ?? true
    });
    setDialog(true);
  };

  const handleDelete = (cat) => {
    confirm({
      title: 'Delete Conduct Category',
      message: `Are you sure you want to delete "${getCategoryName(cat)}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await reportCardAPI.deleteConductCategory(cat.id);
          showSnackbar('Category deleted successfully');
          fetchCategories();
        } catch (e) {
          showSnackbar(getErrorMessage(e, 'Failed to delete category'), 'error');
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const name = form.category_name.trim();
    if (!name) { showSnackbar('Category name is required', 'error'); return; }
    const code = (form.category_code || name).toUpperCase().replace(/\s+/g, '_').slice(0, 20);
    setSaving(true);
    try {
      const payload = {
        category_code: code,
        category_name: name,
        name,
        display_order: form.display_order === '' ? null : Number(form.display_order),
        description: form.description || null,
        is_active: form.is_active ?? true
      };
      if (editingCategory) {
        await reportCardAPI.updateConductCategory(editingCategory.id, payload);
        showSnackbar('Category updated successfully');
      } else {
        await reportCardAPI.createConductCategory(payload);
        showSnackbar('Category created successfully');
      }
      resetForm();
      fetchCategories();
    } catch (e) {
      showSnackbar(getErrorMessage(e, 'Failed to save category'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // client-side pagination
  const total = categories.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, lastPage);
  const paged = categories.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pagination = { current_page: currentPage, last_page: lastPage, per_page: pageSize, total };

  return (
    <Box>
      <ModulePageHeader
        title="Conduct Categories"
        subtitle="Manage conduct category types (e.g. Attendance, Behaviour)"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<Refresh />} onClick={fetchCategories} disabled={loading} fullWidth={isMobile}>Refresh</Button>,
          <Button key="bulk" variant="outlined" startIcon={<Add />} onClick={() => setBulkDialog(true)} fullWidth={isMobile}>Bulk Add</Button>,
          <Button key="add" variant="contained" startIcon={<Add />} onClick={() => setDialog(true)} fullWidth={isMobile}>Add Category</Button>
        ]}
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
          ) : categories.length === 0 ? (
            <EmptyRecordsState message="No conduct categories found." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList
                items={paged}
                renderItem={(cat) => (
                  <Stack spacing={0.75}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={700}>{getCategoryName(cat)}</Typography>
                      <Chip label={getCategoryCode(cat)} size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">Order: {cat.display_order ?? '-'}</Typography>
                    <Typography variant="body2" color="text.secondary">{cat.description || 'No description'}</Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Chip label={cat.is_active ? 'Active' : 'Inactive'} color={cat.is_active ? 'success' : 'default'} size="small" />
                      <Box>
                        <IconButton size="small" color="primary" onClick={() => handleEdit(cat)}><EditIcon /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(cat)}><DeleteIcon /></IconButton>
                      </Box>
                    </Box>
                  </Stack>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile />
            </>
          ) : (
            <>
              <DataTable
                columns={COLUMNS}
                data={paged}
                loading={false}
                emptyMessage="No conduct categories found."
                minWidth={700}
                renderRow={(cat) => (
                  <TableRow key={cat.id} hover>
                    <TableCell sx={TD_SX}><strong>{getCategoryCode(cat)}</strong></TableCell>
                    <TableCell sx={TD_SX}>{getCategoryName(cat)}</TableCell>
                    <TableCell sx={TD_SX}>{cat.display_order ?? '-'}</TableCell>
                    <TableCell sx={TD_SX}>{cat.description || '-'}</TableCell>
                    <TableCell sx={TD_SX}>
                      <Chip label={cat.is_active ? 'Active' : 'Inactive'} color={cat.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell sx={TD_SX}>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(cat)}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(cat)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile={isMobile} />
            </>
          )}
        </CardContent>
      </Card>

      <ConductCategoryFormDialog
        open={dialog}
        onClose={resetForm}
        onSubmit={handleSubmit}
        editingCategory={editingCategory}
        formData={form}
        setFormData={setForm}
        saving={saving}
        isMobile={isMobile}
      />

      <BulkConductCategoryDialog
        open={bulkDialog}
        onClose={() => setBulkDialog(false)}
        saving={savingBulk}
        isMobile={isMobile}
        onSubmit={async (rows, onSuccess) => {
          setSavingBulk(true);
          try {
            await Promise.all(rows.map((r) => {
              const name = r.category_name.trim();
              const code = (r.category_code || name).toUpperCase().replace(/\s+/g, '_').slice(0, 20);
              return reportCardAPI.createConductCategory({
                category_code: code, category_name: name, name,
                display_order: r.display_order === '' ? null : Number(r.display_order),
                description: r.description || null, is_active: true
              });
            }));
            showSnackbar(`${rows.length} categor${rows.length === 1 ? 'y' : 'ies'} created successfully`);
            setBulkDialog(false);
            onSuccess();
            fetchCategories();
          } catch (e) {
            showSnackbar(getErrorMessage(e, 'Bulk creation failed'), 'error');
          } finally {
            setSavingBulk(false);
          }
        }}
      />
      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
}
